import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { createOrganization } from "./projectsSlice";

// Thunks
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (organizationId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/organization/${organizationId}/role`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteUserFromOrganization = createAsyncThunk(
  "users/deleteUserFromOrganization",
  async ({ organizationId, userId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/organization/${organizationId}/role/${userId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteUserFromOrganizationAndProject = createAsyncThunk(
  "users/deleteUserFromOrganization",
  async ({ organizationId, userId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/organization/${organizationId}/user/${userId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const inviteUserToOrganization = createAsyncThunk(
  "users/inviteUserToOrganization",
  async ({ organizationId, userInfo }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/organization/${organizationId}/role`,
        userInfo
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "users/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/api/v1/me");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchCurrentUserForAvatar = createAsyncThunk(
  "users/fetchCurrentUserForAvatar",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/api/v1/me");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  "users/loginUser",
  async ({ redirectUri = "" }, { rejectWithValue }) => {
    const urlString = redirectUri
      ? `/api/v1/auth/login?redirect=${redirectUri}`
      : "/api/v1/auth/login";
    try {
      const response = await axiosInstance.get(urlString);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getAccessToken = createAsyncThunk(
  "users/getAccessToken",
  async (code, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/auth/callback?code=${code}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const refreshUserToken = createAsyncThunk(
  "users/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/api/v1/auth/refresh", {});
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "users/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/api/v1/auth/logout");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, ...userData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/v1/me/profile`, userData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const setUserAvatar = createAsyncThunk(
  "users/setUserAvatar",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/v1/me/avatar`, formData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateOrganization = createAsyncThunk(
  "users/updateOrganization",
  async ({ id, ...orgData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/organization/${id}`,
        orgData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "users/updatePassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/v1/me/password`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/users/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteOrganization = createAsyncThunk(
  "users/deleteOrganization",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/v1/organization/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchSharedLinks = createAsyncThunk(
  "users/fetchSharedLinks",
  async ({ resourceType, resourceId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/share?resourceType=${resourceType}&resourceId=${resourceId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const revokeSharedLink = createAsyncThunk(
  "users/revokeSharedLink",
  async ({ linkId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/v1/share/${linkId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const processShareToken = createAsyncThunk(
  "users/processShareToken",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/share/access?token=${token}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const validateShareToken = createAsyncThunk(
  "users/validateShareToken",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/share/validate?token=${token}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const assignRoleFromSharing = createAsyncThunk(
  "users/assignRoleFromSharing",
  async ({ contentVersionId }, { rejectWithValue }) => {
    try {
      let response;

      response = await axiosInstance.post(`/api/v1/share/assign`, {
        contentVersionId,
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "users/resetPassword",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/me/password/reset?redirectUrl=${encodeURIComponent(
          "https://go.7cut.video/security"
        )}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getSessionsList = createAsyncThunk(
  "users/getSessionsList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/me/session`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetAllSessions = createAsyncThunk(
  "users/resetAllSessions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/v1/me/session/logout`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetCurrentOrgOwner = () => ({
  type: "RESET_CURRENT_ORG_OWNER",
});

export const generateShareLink = createAsyncThunk(
  "users/generateShareLink",
  async ({ resourceId, resourceType, role, email }, { rejectWithValue }) => {
    let apiLink = `/api/v1/share/generate?resourceId=${resourceId}&resourceType=${resourceType}&role=${role}`;
    if (email) {
      apiLink += `&email=${email}`;
    }
    try {
      const response = await axiosInstance.post(apiLink);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchUserProjectRoles = createAsyncThunk(
  "users/fetchUserProjectRoles",
  async ({ organizationId, userId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/organization/${organizationId}/project/role/${userId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteUserProjectRole = createAsyncThunk(
  "users/deleteUserProjectRole",
  async ({ roleId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/project-role/${roleId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const setCommentReaction = createAsyncThunk(
  "users/setCommentReaction",
  async ({ commentId, reactionId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/comment/${commentId}/reactions/${reactionId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const removeCommentReaction = createAsyncThunk(
  "users/removeCommentReaction",
  async ({ commentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/comment/${commentId}/reactions`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchReactionsPacks = createAsyncThunk(
  "users/fetchReactionsPacks",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/admin/reaction-packs`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchReactions = createAsyncThunk(
  "users/fetchReactions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/admin/reactions`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createReaction = createAsyncThunk(
  "users/createReaction",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/reactions`,
        formData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getOrganizationReactions = createAsyncThunk(
  "users/getOrganizationReactions",
  async ({ organizationId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/organization/${organizationId}/reactions`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAgreements = createAsyncThunk(
  "users/fetchAgreements",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/admin/legal-document`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAgreement = createAsyncThunk(
  "users/fetchAgreement",
  async (agreementId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/admin/legal-document/${agreementId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAgreementVersions = createAsyncThunk(
  "users/fetchAgreementVersions",
  async (agreementId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/admin/legal-document/${agreementId}/version`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchNeededAgreements = createAsyncThunk(
  "users/fetchNeededAgreements",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/legal-document/required`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const acceptNeededAgreement = createAsyncThunk(
  "users/acceptNeededAgreement",
  async (versionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/legal-document/version/${versionId}/accept`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createAgreementVersion = createAsyncThunk(
  "users/createAgreementVersion",
  async ({ agreementId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/legal-document/${agreementId}/version`,
        formData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteAgreementVersion = createAsyncThunk(
  "users/deleteAgreementVersion",
  async ({ agreementId, documentVersionId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/admin/legal-document/${agreementId}/version/${documentVersionId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const publishDocumentVersion = createAsyncThunk(
  "users/publishDocumentVersion",
  async ({ agreementId, documentVersionId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/legal-document/${agreementId}/version/${documentVersionId}/publish`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createDocumentPresign = createAsyncThunk(
  "users/createDocumentPresign",
  async (
    { agreementId, documentVersionId, contentType },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/legal-document/${agreementId}/version/${documentVersionId}/document/presign`,
        { contentType }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const completeDocumentUpload = createAsyncThunk(
  "users/createDocumentPresign",
  async ({ agreementId, documentVersionId, key }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/legal-document/${agreementId}/version/${documentVersionId}/document/complete`,
        { key }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createAgreement = createAsyncThunk(
  "users/createAgreement",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/legal-document`,
        formData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteAgreement = createAsyncThunk(
  "users/deleteAgreement",
  async (agreementId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/admin/legal-document/${agreementId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createReactionPack = createAsyncThunk(
  "users/createReactionPack",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/admin/reaction-packs`,
        formData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteReaction = createAsyncThunk(
  "users/deleteReaction",
  async (reactionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/admin/reactions/${reactionId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createUserSubscription = createAsyncThunk(
  "users/createUserSubscription",
  async ({ organizationId, subscriptionData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/organization/${organizationId}/subscription`,
        subscriptionData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState: {
    items: [],
    current: null,
    currentUser: null,
    currentOrganization: null,
    currentOrganizationOwner: null,
    currentOrganizationProjectRoles: [],
    organizationsList: [],
    currentUserRoles: [],
    loading: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.items = action.payload;
        const orgOwner = action.payload.find(
          (userInfo) => userInfo.role === "OWNER"
        );
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const idx = state.items.findIndex((u) => u.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
        state.currentUser = action.payload;
      })
      .addCase("SET_CURRENT_ORGANIZATION", (state, action) => {
        state.currentOrganization = action.payload;
        const ownerIndex = state.currentUser.organizationRoles.findIndex(
          (orgRole) =>
            orgRole.role === "OWNER" &&
            orgRole.organizationId === state.currentOrganization.id
        );
        if (ownerIndex > -1) {
          state.currentOrganizationOwner = {
            id: state.currentUser.id,
          };
        } else {
          state.currentOrganizationOwner = null;
        }
        localStorage.setItem("currentOrganizationId", action.payload.id);
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        const newOrgList = [...state.organizationsList];

        const orgIndex = newOrgList.findIndex(
          (org) => org.id === action.payload.id
        );

        if (orgIndex > -1) {
          newOrgList[orgIndex].title = action.payload.title;
        }

        if (
          !!state.currentOrganization &&
          action.payload.id === state.currentOrganization.id
        ) {
          state.currentOrganization.title = action.payload.title;
        }

        state.organizationsList = newOrgList;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        const newOrgList = [...state.organizationsList];

        const orgIndex = newOrgList.findIndex(
          (org) => org.id == action.payload.id || org.id === action.payload
        );

        if (orgIndex > -1) {
          newOrgList.splice(orgIndex, 1);
        }

        if (
          !!state.currentOrganization &&
          action.payload.id === state.currentOrganization.id
        ) {
          state.currentOrganization = newOrgList[0];
        }

        state.organizationsList = newOrgList;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.items = state.items.filter((u) => u.id !== action.payload);
      })
      .addCase(fetchUserProjectRoles.fulfilled, (state, action) => {
        state.currentOrganizationProjectRoles = action.payload;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.organizationsList = [...state.organizationsList, action.payload];
      })
      .addCase(fetchCurrentUserForAvatar.fulfilled, (state, action) => {
        state.currentUser.avatar = action.payload.avatar;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        const currentOrganizationId = localStorage.getItem(
          "currentOrganizationId"
        );
        if (currentOrganizationId) {
          const currentOrg = action.payload.organizations.find(
            (org) => org.id === +currentOrganizationId
          );
          if (currentOrg) {
            state.currentOrganization = currentOrg;
          } else {
            state.currentOrganization = action.payload.organizations[0];
            localStorage.setItem(
              "currentOrganizationId",
              action.payload.organizations[0].id
            );
          }
        } else {
          state.currentOrganization = action.payload.organizations[0];
          localStorage.setItem(
            "currentOrganizationId",
            action.payload.organizations[0].id
          );
        }
        const ownerIndex = state.currentUser.organizationRoles.findIndex(
          (orgRole) =>
            orgRole.role === "OWNER" &&
            orgRole.organizationId === state.currentOrganization.id
        );
        if (ownerIndex > -1) {
          state.currentOrganizationOwner = {
            id: state.currentUser.id,
          };
        } else {
          state.currentOrganizationOwner = null;
        }

        state.organizationsList = action.payload.organizations;
      });
  },
});

export default usersSlice.reducer;
